import type { InvoiceRow } from '@/state/billing/useInvoices';
import type { BillingSettingsManager } from '@/state/billing/useBillingSettings';
import { DataTable, useDataControl } from '@/components/DataTable';
import { cn } from 'helpers/cn';
import { formatMinorUnits } from 'helpers/formatters';
import { formatBillingDate } from '@/state/billing/billing-format';
import { MICRO_LABEL_SHAPE, Panel } from '../../ConsolePanel';
import { SectionStatusLine } from './SectionStatusLine';

type InvoicesTableProps = { manager: BillingSettingsManager };

const BILLING_REASON_LABEL: Record<string, string> = {
  subscription_create: 'First payment',
  subscription_cycle: 'Renewal',
  subscription_update: 'Plan change',
  purchase: 'Purchase',
};

/** The account's own invoices: the plan, plan changes and credit packs. The receipt itself is in Polar's portal. */
export const InvoicesTable = ({ manager }: InvoicesTableProps) => {
  const { invoices: section, currency } = manager;
  const { invoices } = section;

  return (
    <Panel tourAnchor="billing-invoices" title="Invoices" description="Charges on this account.">
      {!invoices ? (
        <SectionStatusLine
          status={section}
          loadingText="Loading invoices..."
          errorText="Invoices could not be loaded."
        />
      ) : invoices.length === 0 ? (
        <p className="supporting m-0">No invoices yet</p>
      ) : (
        <InvoiceRows invoices={invoices} currency={currency} />
      )}
    </Panel>
  );
};

type InvoiceRowsProps = { invoices: InvoiceRow[]; currency: string | null };

const InvoiceRows = ({ invoices, currency }: InvoiceRowsProps) => {
  const dataController = useDataControl<InvoiceRow>(invoices);

  return (
    <DataTable<InvoiceRow>
      dataController={dataController}
      uniqueKey="id"
      columns={[
        {
          label: 'Date',
          field: 'createdAt',
          sortKey: 'createdAt',
          mobile: 'title',
          format: ({ item }) => <span className="num">{formatBillingDate(item.createdAt)}</span>,
        },
        {
          label: 'Amount',
          field: 'amount',
          sortKey: 'amount',
          format: ({ item }) => (
            <span className="num">
              {item.amount != null ? formatMinorUnits(item.amount, item.currency ?? currency) : '-'}
              {!!item.refundedAmount && (
                <span className="supporting block">
                  {formatMinorUnits(item.refundedAmount, item.currency ?? currency)} refunded
                </span>
              )}
            </span>
          ),
        },
        {
          label: 'For',
          field: 'billingReason',
          format: ({ item }) =>
            item.billingReason ? (BILLING_REASON_LABEL[item.billingReason] ?? item.billingReason) : '-',
        },
        {
          label: 'Status',
          field: 'paid',
          sortKey: 'paid',
          format: ({ item }) => (
            <span
              className={cn(
                MICRO_LABEL_SHAPE,
                'rounded-pill inline-flex w-fit border px-2 py-0.5',
                item.paid ? 'border-ok-line text-ok' : 'border-warn-line text-warn',
              )}
            >
              {item.paid ? 'Paid' : 'Pending'}
            </span>
          ),
        },
      ]}
    />
  );
};
